import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/* ── Small toggle switch component ── */
function ToggleSwitch({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className="relative inline-flex items-center flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200"
      style={{
        width: "44px",
        height: "24px",
        backgroundColor: value ? "#7C3AED" : "#9CA3AF",
      }}
    >
      <span
        className="rounded-full bg-white shadow-sm transition-transform duration-200"
        style={{
          width: "18px",
          height: "18px",
          transform: value ? "translateX(23px)" : "translateX(3px)",
          display: "block",
          flexShrink: 0,
        }}
      />
    </button>
  );
}

function BackgroundBlurToggle() {
  const [on, setOn] = useState(false);
  return <ToggleSwitch value={on} onChange={setOn} />;
}

function HdToggle() {
  const [on, setOn] = useState(true);
  return <ToggleSwitch value={on} onChange={setOn} />;
}

function MirrorToggle({
  mirrored,
  onChange,
}: {
  mirrored: boolean;
  onChange: (v: boolean) => void;
}) {
  return <ToggleSwitch value={mirrored} onChange={onChange} />;
}

interface LobbyState {
  mentorName: string;
  mentorRole: string;
  mentorCompany: string;
  mentorPhoto: string;
  date: string;
  time: string;
}

function getBookedMentor(): LobbyState {
  try {
    const raw = localStorage.getItem("mentorAI_bookedMentor");
    if (raw) {
      const m = JSON.parse(raw) as {
        name?: string; mentorName?: string;
        role?: string; mentorRole?: string;
        company?: string; mentorCompany?: string;
        photo?: string; mentorPhoto?: string;
        date?: string; time?: string;
      };
      const resolvedName = m.name ?? m.mentorName ?? "";
      if (resolvedName) {
        return {
          mentorName: resolvedName,
          mentorRole: m.role ?? m.mentorRole ?? "Mentor",
          mentorCompany: m.company ?? m.mentorCompany ?? "",
          mentorPhoto: m.photo ?? m.mentorPhoto ?? "https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20smiling%20mentor%2C%20soft%20studio%20lighting%2C%20clean%20white%20background%2C%20business%20casual%20attire%2C%20confident%20expression%2C%20high%20quality%20portrait%20photography&width=400&height=400&seq=mentor-default&orientation=squarish",
          date: m.date ?? "April 14, 2026",
          time: m.time ?? "10:00 AM",
        };
      }
    }
  } catch (_) { /* ignore */ }
  return {
    mentorName: "Your Mentor",
    mentorRole: "Mentor",
    mentorCompany: "",
    mentorPhoto: "https://readdy.ai/api/search-image?query=professional%20headshot%20of%20a%20smiling%20mentor%2C%20soft%20studio%20lighting%2C%20clean%20white%20background%2C%20business%20casual%20attire%2C%20confident%20expression%2C%20high%20quality%20portrait%20photography&width=400&height=400&seq=mentor-default&orientation=squarish",
    date: "April 14, 2026",
    time: "10:00 AM",
  };
}

export default function MeetingLobbyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session: LobbyState = (location.state as LobbyState) || getBookedMentor();

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [mirrored, setMirrored] = useState(true);
  const [camError, setCamError] = useState(false);
  const [joining, setJoining] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (camOn) {
      setCamError(false);
      navigator.mediaDevices
        .getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false })
        .then((stream) => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch(() => {
          setCamError(true);
        });
    } else {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [camOn]);

  const handleJoin = () => { setJoining(true); setCountdown(3); };

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) { navigate("/video-room", { state: session }); return; }
    const t = setTimeout(() => setCountdown((c) => (c !== null ? c - 1 : null)), 1000);
    return () => clearTimeout(t);
  }, [countdown, navigate, session]);

  const agenda = [
    "Intro & goal alignment (5 min)",
    "Review your portfolio & current projects (20 min)",
    "Identify skill gaps for UX transition (15 min)",
    "Build a 30-day action plan together (15 min)",
    "Q&A and next steps (5 min)",
  ];

  const checklist = [
    { label: "Resume shared with mentor", done: true },
    { label: "Goal summary sent", done: true },
    { label: "AI Scribe enabled", done: true },
    { label: "Quiet environment", done: false },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg-base)" }}>
      <nav className="flex items-center justify-between px-8 py-4 border-b" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border)" }}>
        <div className="flex items-center gap-3">
          {/* Logo icon — purple rounded square with sparkle */}
          <div
            className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0"
            style={{ backgroundColor: "#7C3AED" }}
          >
            <i className="ri-sparkling-2-fill text-white text-base" />
          </div>
          <span className="font-bold text-xl tracking-tight" style={{ color: "#7C3AED" }}>GrowthFlow</span>
        </div>
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Session starting soon
        </div>
      </nav>

      <main className="flex-1 px-6 py-10 max-w-6xl mx-auto w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: "var(--text-primary)" }}>Your session is ready</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
            Check your setup below, then join when you&apos;re ready.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* LEFT — Camera preview + controls */}
          <div className="lg:col-span-3 flex flex-col gap-5">
            <div className="relative bg-gray-900 rounded-2xl overflow-hidden aspect-video w-full">
              {/* Live self-view — always rendered so the ref is attached; hidden when cam is off or errored */}
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover transition-transform duration-200"
                style={{
                  transform: mirrored ? "scaleX(-1)" : "scaleX(1)",
                  display: camOn && !camError ? "block" : "none",
                }}
              />

              {/* Camera off state */}
              {!camOn && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-700">
                    <i className="ri-video-off-line text-gray-400 text-3xl" />
                  </div>
                  <p className="text-gray-400 text-sm">Camera is off</p>
                </div>
              )}

              {/* Camera permission denied */}
              {camOn && camError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-red-900/40">
                    <i className="ri-camera-off-line text-red-400 text-3xl" />
                  </div>
                  <p className="text-gray-300 text-sm font-medium">Camera access denied</p>
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Allow camera access in your browser settings, then toggle the camera button below.
                  </p>
                </div>
              )}

              {/* Name label */}
              <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur-sm">
                <p className="text-white text-xs font-medium">You (Mentee)</p>
              </div>

              {/* Mic status indicator */}
              <div className="absolute top-4 right-4">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full backdrop-blur-sm ${micOn ? "bg-emerald-500/90" : "bg-red-500/90"}`}>
                  <i className={`${micOn ? "ri-mic-line" : "ri-mic-off-line"} text-white text-sm`} />
                </div>
              </div>

              {/* Mirror indicator badge — only when cam is on */}
              {camOn && !camError && (
                <div className="absolute top-4 left-4 px-2 py-1 rounded-lg bg-black/40 backdrop-blur-sm flex items-center gap-1.5">
                  <i className="ri-flip-horizontal-line text-gray-300 text-xs" />
                  <span className="text-gray-300 text-xs">{mirrored ? "Mirrored" : "Normal"}</span>
                </div>
              )}
            </div>

            <div className="rounded-2xl border p-5" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border)" }}>
              <p className="text-sm font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Pre-join settings</p>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setMicOn((v) => !v)}
                  className="flex-1 flex items-center justify-center gap-2.5 py-3 rounded-xl font-medium text-sm transition-all cursor-pointer whitespace-nowrap border"
                  style={micOn
                    ? { backgroundColor: "var(--success-light)", color: "var(--success)", borderColor: "var(--success)" }
                    : { backgroundColor: "var(--danger-light)", color: "var(--danger)", borderColor: "var(--danger)" }
                  }
                >
                  <i className={micOn ? "ri-mic-line" : "ri-mic-off-line"} />
                  {micOn ? "Mic On" : "Mic Off"}
                </button>
                <button
                  type="button"
                  onClick={() => setCamOn((v) => !v)}
                  className="flex-1 flex items-center justify-center gap-2.5 py-3 rounded-xl font-medium text-sm transition-all cursor-pointer whitespace-nowrap border"
                  style={camOn
                    ? { backgroundColor: "var(--success-light)", color: "var(--success)", borderColor: "var(--success)" }
                    : { backgroundColor: "var(--danger-light)", color: "var(--danger)", borderColor: "var(--danger)" }
                  }
                >
                  <i className={camOn ? "ri-video-line" : "ri-video-off-line"} />
                  {camOn ? "Camera On" : "Camera Off"}
                </button>
                <button
                  type="button"
                  className="flex-1 flex items-center justify-center gap-2.5 py-3 rounded-xl font-medium text-sm transition-all cursor-pointer whitespace-nowrap border"
                  style={{ backgroundColor: "var(--bg-elevated)", color: "var(--text-secondary)", borderColor: "var(--border)" }}
                >
                  <i className="ri-volume-up-line" />
                  Test Audio
                </button>
              </div>

              {/* Camera settings — only visible when camera is on */}
              {camOn && (
                <div
                  className="mt-4 pt-4 border-t"
                  style={{ borderColor: "var(--border)" }}
                >
                  <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
                    Camera Settings
                  </p>
                  <div className="flex flex-col gap-3">
                    {/* Background blur */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: "var(--accent-light)" }}>
                          <i className="ri-blur-off-line text-xs" style={{ color: "var(--accent-text)" }} />
                        </div>
                        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Background Blur</span>
                      </div>
                      <BackgroundBlurToggle />
                    </div>
                    {/* Mirror video */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: "var(--accent-light)" }}>
                          <i className="ri-flip-horizontal-line text-xs" style={{ color: "var(--accent-text)" }} />
                        </div>
                        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Mirror Video</span>
                      </div>
                      <MirrorToggle mirrored={mirrored} onChange={setMirrored} />
                    </div>
                    {/* HD quality */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: "var(--accent-light)" }}>
                          <i className="ri-hd-line text-xs" style={{ color: "var(--accent-text)" }} />
                        </div>
                        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>HD Quality</span>
                      </div>
                      <HdToggle />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border p-5" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border)" }}>
              <p className="text-sm font-semibold mb-4" style={{ color: "var(--text-primary)" }}>Pre-session checklist</p>
              <div className="grid grid-cols-2 gap-3">
                {checklist.map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                      style={item.done ? { backgroundColor: "var(--success)" } : { border: "2px solid var(--border)" }}>
                      {item.done && <i className="ri-check-line text-white text-xs" />}
                    </div>
                    <span className="text-sm" style={{ color: item.done ? "var(--text-secondary)" : "var(--text-muted)" }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT — Session details */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="rounded-2xl border p-5" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>Your Mentor</p>
              <div className="flex items-center gap-4 mb-5">
                <img src={session.mentorPhoto} alt={session.mentorName} className="w-14 h-14 rounded-full object-cover object-top border-2" style={{ borderColor: "var(--accent-light)" }} />
                <div>
                  <h3 className="font-bold text-base" style={{ color: "var(--text-primary)" }}>{session.mentorName}</h3>
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>{session.mentorRole}</p>
                  <p className="text-sm font-medium" style={{ color: "var(--accent-text)" }}>{session.mentorCompany}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => <i key={s} className="ri-star-fill text-amber-400 text-sm" />)}
                <span className="text-xs ml-1" style={{ color: "var(--text-muted)" }}>5.0 · 47 sessions</span>
              </div>
            </div>

            <div className="rounded-2xl border p-5" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>Session Details</p>
              <div className="space-y-3.5">
                {[
                  { icon: "ri-calendar-line", label: "Date", value: session.date },
                  { icon: "ri-time-line", label: "Time", value: session.time },
                  { icon: "ri-timer-line", label: "Duration", value: "60 minutes" },
                  { icon: "ri-video-line", label: "Format", value: "Video Call (In-App)" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "var(--accent-light)" }}>
                      <i className={`${item.icon} text-sm`} style={{ color: "var(--accent)" }} />
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>{item.label}</p>
                      <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border p-5" style={{ backgroundColor: "var(--bg-surface)", borderColor: "var(--border)" }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>Session Agenda</p>
              <ol className="space-y-2.5">
                {agenda.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 mt-0.5" style={{ backgroundColor: "var(--accent-light)", color: "var(--accent-text)" }}>
                      {i + 1}
                    </span>
                    <span className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item}</span>
                  </li>
                ))}
              </ol>
            </div>

            <button
              type="button"
              onClick={handleJoin}
              disabled={joining}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 text-white font-bold text-base hover:from-violet-700 hover:to-violet-600 transition-all duration-200 hover:scale-[1.02] disabled:opacity-80 cursor-pointer whitespace-nowrap relative overflow-hidden"
            >
              {joining && countdown !== null && countdown > 0 ? (
                <span className="flex items-center justify-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white font-black text-lg">{countdown}</span>
                  Joining session...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-video-line text-lg" />
                  Join Session Now
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate("/session-dashboard")}
              className="w-full py-3 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
            >
              Back to My Sessions
            </button>
          </div>
        </div>
      </main>

      {joining && countdown !== null && countdown > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="text-center">
            <div className="w-28 h-28 flex items-center justify-center rounded-full border-4 border-violet-400 mb-6 mx-auto" style={{ background: "rgba(139,92,246,0.15)" }}>
              <span className="text-6xl font-black text-white">{countdown}</span>
            </div>
            <p className="text-white text-xl font-semibold">Joining your session...</p>
            <p className="text-violet-300 text-sm mt-2">Connecting with {session.mentorName}</p>
          </div>
        </div>
      )}
    </div>
  );
}
